export type SttMessageSource = "stt_final" | "stt_result";

export interface SttCorrectionDraft {
  rawText: string;
  text: string;
  source: SttMessageSource;
  lowConfidence?: boolean;
  confidence?: number | null;
  threshold?: number | null;
  recognitionClientId?: string | null;
}

export type SttCorrectionDraftDisposition =
  | "editing"
  | "submitted"
  | "ignored"
  | boolean
  | void;

export interface SttMessagePayload {
  transcript?: unknown;
  recognitionClientId?: unknown;
  lowConfidence?: unknown;
  confidence?: unknown;
  threshold?: unknown;
}

export interface SttNotificationHandle {
  dismiss: () => void;
}

export interface InterviewSttMessageDependencies {
  now: () => number;
  isDocumentVisible: () => boolean;
  getCurrentRecognitionClientId: () => string | null;
  getPreviousInterimText: () => string;
  setPreviousInterimText: (text: string) => void;
  getSessionMessageId: () => string | null;
  resetTranscriptState: () => void;
  setCurrentInterimText: (text: string) => void;
  setIsAwaitingResponse: (value: boolean) => void;
  setIsRecording: (value: boolean) => void;
  setRecordingTime: (value: number) => void;
  isRecording: () => boolean;
  isStopping: () => boolean;
  stopRecording: (reason: 'user' | 'silence') => void | Promise<void>;
  onSttCorrectionDraft?: (draft: SttCorrectionDraft) => SttCorrectionDraftDisposition;
  markSttAlive: () => void;
  toast: (opts: { title: string; description: string; duration?: number }) => SttNotificationHandle | void;
  debugLog: (...args: unknown[]) => void;
  interimRenderThrottleMs?: number;
  emptySttNotificationThrottleMs?: number;
  stallHintMs?: number;
}

const DEFAULT_INTERIM_RENDER_THROTTLE_MS = 150;
const DEFAULT_EMPTY_STT_NOTIFICATION_THROTTLE_MS = 1_500;
const DEFAULT_STALL_HINT_MS = 12_000;

/**
 * STTメッセージの表示・確定・空結果・無進捗監視を管理する。
 * WebSocket、React state、録音停止の実処理はdependenciesへ委譲する。
 */
export class InterviewSttMessageController {
  private lastInterimRenderAt = 0;
  private lastEmptySttReleaseAt = 0;
  private lastSttActivityAt = 0;
  private stallHintShown = false;
  private activeSttNotification: SttNotificationHandle | null = null;
  private wasRecordingOnPreviousStallCheck = false;
  private readonly interimRenderThrottleMs: number;
  private readonly emptySttNotificationThrottleMs: number;
  private readonly stallHintMs: number;

  constructor(private readonly dependencies: InterviewSttMessageDependencies) {
    this.interimRenderThrottleMs =
      dependencies.interimRenderThrottleMs ?? DEFAULT_INTERIM_RENDER_THROTTLE_MS;
    this.emptySttNotificationThrottleMs =
      dependencies.emptySttNotificationThrottleMs ?? DEFAULT_EMPTY_STT_NOTIFICATION_THROTTLE_MS;
    this.stallHintMs = dependencies.stallHintMs ?? DEFAULT_STALL_HINT_MS;
  }

  handleInterim(payload: SttMessagePayload): void {
    const { transcript, recognitionClientId } = payload;
    const currentRecognitionClientId = this.dependencies.getCurrentRecognitionClientId();
    if (
      typeof recognitionClientId === "string"
      && currentRecognitionClientId
      && recognitionClientId !== currentRecognitionClientId
    ) {
      this.dependencies.debugLog(
        '[Client] stt_interim: stale recognitionClientId, skipping render',
        { recognitionClientId, currentRecognitionClientId },
      );
      return;
    }

    const displayText = typeof transcript === "string" ? transcript.trim() : "";
    this.markActivity();
    // 中間結果が届いた時点で、recognition_started未受信環境でもSTT生存を確定できる。
    this.dependencies.markSttAlive();
    if (!displayText) {
      this.dependencies.debugLog('[Client] stt_interim: Empty transcript, skipping');
      return;
    }
    this.dismissSttNotification();

    const previousInterim = this.dependencies.getPreviousInterimText();
    if (previousInterim === displayText) {
      this.dependencies.debugLog('[Client] stt_interim: Same text repeated, skipping render');
      return;
    }

    const now = this.dependencies.now();
    if (now - this.lastInterimRenderAt < this.interimRenderThrottleMs) {
      this.dependencies.debugLog('[Client] stt_interim: Throttled render', {
        transcript: displayText,
        elapsedMs: now - this.lastInterimRenderAt,
      });
      return;
    }
    this.lastInterimRenderAt = now;

    this.dependencies.debugLog('[Client] stt_interim processing:', {
      transcript: displayText,
      previousInterim,
      sessionMessageId: this.dependencies.getSessionMessageId(),
    });
    this.dependencies.setPreviousInterimText(displayText);
    this.dependencies.setCurrentInterimText(displayText);
  }

  handleFinal(source: SttMessageSource, payload: SttMessagePayload): void {
    this.markActivity();
    const transcript = typeof payload.transcript === "string" ? payload.transcript.trim() : "";
    if (!transcript) {
      this.releaseAwaitingResponseForEmptyStt(source);
      return;
    }
    this.dismissSttNotification();

    this.dependencies.debugLog(
      `[Client] ${source} received; waiting for text correction:`,
      transcript,
    );
    this.dependencies.resetTranscriptState();
    this.dependencies.setIsRecording(false);
    this.dependencies.setRecordingTime(0);

    const disposition = this.dependencies.onSttCorrectionDraft?.({
      rawText: transcript,
      text: transcript,
      source,
      lowConfidence: payload.lowConfidence === true,
      confidence: typeof payload.confidence === "number" ? payload.confidence : null,
      threshold: typeof payload.threshold === "number" ? payload.threshold : null,
      recognitionClientId:
        typeof payload.recognitionClientId === "string" ? payload.recognitionClientId : null,
    });
    const isSubmitted = disposition === "submitted";
    const isIgnored = disposition === false || disposition === "ignored";

    if (isIgnored) {
      this.dependencies.debugLog(`[Client] ${source}: correction draft ignored by page callback`);
    }
    if (!isSubmitted && !isIgnored) {
      this.dependencies.setIsAwaitingResponse(false);
    }

    if (source === "stt_final" && this.dependencies.isRecording() && !this.dependencies.isStopping()) {
      void Promise.resolve(this.dependencies.stopRecording('silence')).finally(() => {
        if (!isSubmitted) {
          this.dependencies.setIsAwaitingResponse(false);
        }
        this.dependencies.setIsRecording(false);
        this.dependencies.setRecordingTime(0);
      });
    }
  }

  checkForStall(): void {
    const recording = this.dependencies.isRecording();
    if (recording && !this.wasRecordingOnPreviousStallCheck) {
      this.lastSttActivityAt = this.dependencies.now();
      this.stallHintShown = false;
    }
    this.wasRecordingOnPreviousStallCheck = recording;

    if (!recording || this.stallHintShown) return;
    if (!this.dependencies.isDocumentVisible()) return;
    if (this.lastSttActivityAt === 0) return;
    if (this.dependencies.now() - this.lastSttActivityAt < this.stallHintMs) return;

    this.stallHintShown = true;
    this.showSttNotification({
      title: 'うまく聞き取れていないようです',
      description: '反応がないときは、一度マイクを押して止め、もう一度押して話してください。',
      duration: 5_000,
    });
  }

  private markActivity(): void {
    this.lastSttActivityAt = this.dependencies.now();
    this.stallHintShown = false;
  }

  private showSttNotification(opts: { title: string; description: string; duration?: number }): void {
    this.dismissSttNotification();
    const handle = this.dependencies.toast(opts);
    this.activeSttNotification = handle && typeof handle.dismiss === 'function' ? handle : null;
  }

  private dismissSttNotification(): void {
    this.activeSttNotification?.dismiss();
    this.activeSttNotification = null;
  }

  private releaseAwaitingResponseForEmptyStt(source: SttMessageSource): void {
    this.dependencies.debugLog(`[Client] ${source}: empty transcript, releasing response wait`);
    const now = this.dependencies.now();
    const shouldNotify = now - this.lastEmptySttReleaseAt > this.emptySttNotificationThrottleMs;
    this.lastEmptySttReleaseAt = now;

    const finishRelease = () => {
      this.dependencies.resetTranscriptState();
      this.dependencies.setIsAwaitingResponse(false);
      this.dependencies.setIsRecording(false);
      this.dependencies.setRecordingTime(0);
      if (shouldNotify) {
        this.showSttNotification({
          title: '音声を認識できませんでした',
          description: 'もう一度マイクを押して話してください。',
          duration: 5_000,
        });
      }
    };

    if (this.dependencies.isRecording() && !this.dependencies.isStopping()) {
      void Promise.resolve(this.dependencies.stopRecording('user')).finally(finishRelease);
      return;
    }
    finishRelease();
  }
}
